import { flattenEdges } from "@pathwright/utils"
import React from "react"
import {
  HomeMentorGroupMembershipFragment,
  HomeRegistrationFragment,
  HomeResourceCopyConfigLinkFragment,
  InboxPendingReviewTarget,
  MentorGroupMembershipRole,
  useHomeMentorGroupMembershipsQuery,
  useHomeRegistrationsQuery,
  useHomeResourceCopyConfigLinkConnectionsQuery,
  useInboxPendingReviewCountsQuery,
  usePositioningContextQuery
} from "@pathwright/graphql-client/src/generated"
import {
  PositionedObject,
  sortPositionedObjects
} from "../positioning/PositioningContext"
import { useResourceCopyConfigLinks } from "./components/prompt/CopyLinkAddPromptCard"
import { useHomeRegistrationVars } from "./registration/HomeRegistrationQueryVars"
import { ConditionalHomeTip } from "./tips/HomeTips"
import { useSuperUserHomeTips } from "./tips/SuperUserHomeTips"
import { filterOutSourceRegistration } from "./utils"

type PendingReviewCounts = {
  id: number
  count: number
}

export type PendingReviewCountItem =
  | HomeRegistrationFragment
  | HomeMentorGroupMembershipFragment

type GetReviewCount = (item: PendingReviewCountItem) => number

type HomeContextValue = {
  loading: boolean
  // Whether we are waiting for registrations to be positioned (a migration from legacy home screen to new home screen).
  // This is to maintain as close to the previous visual order as possible.
  awaitingPositionedRegistrations: boolean
  registrations: PositionedObject<HomeRegistrationFragment>[]
  registrationsQuery: ReturnType<typeof useHomeRegistrationsQuery>
  groupMemberships: HomeMentorGroupMembershipFragment[]
  groupMembershipsQuery: ReturnType<typeof useHomeMentorGroupMembershipsQuery>
  copyLinks: HomeResourceCopyConfigLinkFragment[]
  copyLinksQuery: ReturnType<
    typeof useHomeResourceCopyConfigLinkConnectionsQuery
  >
  tips: (JSX.Element | null | undefined)[]
  // TODO: refactor pending review counts into their own context, which would isolate
  // concerns and map better 1:1 with usage (i.e. wrapping groupMemberships list and
  // registrations list separately with their own pending review contexts).
  // We could then use those contexts more naturally elsewhere if needed.
  // For instance, PathCardRegistrationControls currently depends on HomeContext for this
  // purpose, but the HomeContext isn't always wrapping the PathCardRegistrationControls
  // (like in the HomeLibraryAugmentation when used elsewhere in the platform).
  pathPendingReviewCounts: PendingReviewCounts[]
  groupPendingReviewCounts: PendingReviewCounts[]
  getPendingReviewCount: GetReviewCount
}

export const HomeContext = React.createContext<HomeContextValue>(
  {} as HomeContextValue
)

export const useHomeContext = () => React.useContext(HomeContext)

export const HomeContextProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const registrationVariables = useHomeRegistrationVars()

  const registrationsQuery = useHomeRegistrationsQuery({
    variables: registrationVariables
  })

  // TODO: can we rely on PositioningContext here instead? Issue is that the PositioningContextProvider
  // requires the registrations to be passed in, which are queried here.
  const positioningContextQuery = usePositioningContextQuery({
    variables: {
      input: registrationVariables.positioningContext!
    },
    skip: !registrationVariables.positioningContext
  })
  const hasPositionedHomeScreen =
    !!positioningContextQuery.data?.positioningContext?.id

  // Fall back to previous data if no current data exists (if registrations are loading again).
  const registrationNodes =
    (
      registrationsQuery.data?.me?.registrations ||
      registrationsQuery.previousData?.me?.registrations
    )?.edges?.map((edge) => edge?.node!) || []

  const registrations = sortPositionedObjects(
    filterOutSourceRegistration(registrationNodes)
  )
  // Are we waiting on registrations to be positioned (a migration from the old home screen to the new one)?
  // Checking for more than 1 registration since positioning 1 registration will not change it's visual position.
  // This helps avoid showing a spinner after the user creates their first Path.
  const awaitingPositionedRegistrations =
    registrations.length > 1 && !hasPositionedHomeScreen

  // Explicit previews replace automatic tips so the same tip cannot appear twice.
  const superUserHomeTips = useSuperUserHomeTips({ registrations })
  const tips = superUserHomeTips.length
    ? superUserHomeTips
    : [<ConditionalHomeTip registrations={registrations} />]

  // Get all copy links, iterate through and render wrappring LayoutItem.
  const [copyLinks, copyLinksQuery] = useResourceCopyConfigLinks()

  // User's admin/mentor group memberships.
  const groupMembershipsQuery = useHomeMentorGroupMembershipsQuery({
    variables: {
      first: 3, // Cropping to 3, prompt to open full list if more exist.
      role: {
        in: [
          MentorGroupMembershipRole.Admin,
          MentorGroupMembershipRole.Moderator
        ]
      },
      // Exclude single seat groups which are used alongside "Individual" Group Subscriptions.
      mentorGroupSeatLimitFilter: { neq: 1 }
    }
  })

  const groupMemberships =
    groupMembershipsQuery.data?.me?.mentorGroupMemberships?.edges?.map(
      (edge) => edge?.node!
    ) || []

  // Path inbox pending review counts.
  const pathInboxPendingReviewCountsQuery = useInboxPendingReviewCountsQuery({
    variables: {
      targetType: InboxPendingReviewTarget.Path,
      targetIds: {
        in: registrations.map((registration) => registration.group.id)
      }
    },
    skip: !registrations.length
  })

  const pathPendingReviewCounts = (flattenEdges(
    pathInboxPendingReviewCountsQuery.data?.inboxPendingReviewConnections
  ) || []) as PendingReviewCounts[]

  // Group inbox pending review counts.
  const groupInboxPendingReviewCountsQuery = useInboxPendingReviewCountsQuery({
    variables: {
      targetType: InboxPendingReviewTarget.Group,
      targetIds: {
        in: groupMemberships?.map((membership) => membership.group.id) || []
      }
    },
    skip: !groupMemberships?.length
  })

  const groupPendingReviewCounts = (flattenEdges(
    groupInboxPendingReviewCountsQuery.data?.inboxPendingReviewConnections
  ) || []) as PendingReviewCounts[]

  // Function to get pending review count by type and id.
  const getPendingReviewCount: GetReviewCount = (item) => {
    let counts: PendingReviewCounts[] = []
    // NOTE: ironically, both item types have the same path to get the target id,
    // but that could change in the future (when we stick to naming patterns).
    let itemTargetId: number | null = null
    if (item.__typename === "Registration") {
      counts = pathPendingReviewCounts
      itemTargetId = item.group.id
    } else if (item.__typename === "MentorGroupMembership") {
      counts = groupPendingReviewCounts
      itemTargetId = item.group.id
    }
    const countEntry = counts.find(
      ({ id: countId }) => countId === itemTargetId
    )
    return countEntry ? countEntry.count : 0
  }

  // NOTE: we keep loading separate from awaitingPositionedRegistrations
  // since loading covers the data fetching state, whereas
  // awaitingPositionedRegistrations is specifically about
  // waiting for the positioning migration to complete and we must render
  // the HomeMain which nests the SortRegistrations component under the PositioningContextProvider.
  // We handle the combined loading state in HomeMain.
  const loading =
    registrationsQuery.loading ||
    groupMembershipsQuery.loading ||
    copyLinksQuery.loading

  const contextValue: HomeContextValue = {
    loading,
    awaitingPositionedRegistrations,
    tips,
    copyLinks,
    copyLinksQuery,
    registrations,
    registrationsQuery,
    groupMemberships,
    groupMembershipsQuery,
    pathPendingReviewCounts,
    groupPendingReviewCounts,
    getPendingReviewCount
  }

  return (
    <HomeContext.Provider value={contextValue}>{children}</HomeContext.Provider>
  )
}
